/**
 * azurePickerStore — persists the user's preferred Azure discovery identity
 * (`az-cli` vs `entra`) so the picker defaults to their last explicit choice on
 * the next launch. Persisted to localStorage via zustand's persist middleware
 * (name `jam-sql-azure-picker`). `undefined` means "auto" — the main process
 * resolves az-cli when available, else Entra.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AzureIdentitySource } from '../../../types/azureDiscovery';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';

export interface AzurePickerStore {
  /** Last identity the user explicitly chose; undefined = auto. */
  preferredIdentity?: AzureIdentitySource;
  setPreferredIdentity: (source: AzureIdentitySource | undefined) => void;
}

export const useAzurePickerStore = create<AzurePickerStore>()(
  persist(
    (set) => ({
      preferredIdentity: undefined,
      setPreferredIdentity: (preferredIdentity) => set({ preferredIdentity }),
    }),
    {
      name: 'jam-sql-azure-picker',
      // Crash-proof write (core/storage/guardedJsonStorage.ts): a full origin must not throw out of
      // set(). No toast here — this is a remembered default the picker falls back to asking for;
      // the drop is still reported to telemetry and the console.
      storage: createGuardedJsonStorage(),
      version: 1,
      partialize: (state) => ({ preferredIdentity: state.preferredIdentity }),
    }
  )
);
